import { Flex, Table, TableProps } from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

// components
import ActionListHeaderComponent from 'modules/main/actions/listHeader';
import TableTotal from 'components/tableTotal/tableTotal';
import NoData from 'components/noData/noData';
import StatusComponent from 'components/status/status';
import ListButton from 'components/listButton/listButton';

// services
import {
	onCancelMeeting,
	onCloseMeeting,
	onEndMeetingVoting,
	onGetMeetingsForActiveBoard,
	onStartMeetingAttendance,
	onStartMeetingVoting,
} from '../services/meetingServices';
import NotificationService from 'core/services/notification';
import { onGetServiceCost } from '../../../commonServices';
import { StatusColors } from 'utils/statusColors';
import { isRTL } from 'core/services/language';
import { dateFormat } from 'utils/dateFormat';

// models
import { ServiceCost } from '../../../commonModels';
import { Meeting } from '../models/meetings';
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';

// hooks
import useCompanySuspended from 'core/hooks/useCompanySuspended';

interface BoardsListPageProps {}

const BoardMeetingsListPage: FunctionComponent<BoardsListPageProps> = () => {
	const { t } = useTranslation();

	const { suspendedProps } = useCompanySuspended();

	const navigate = useNavigate();
	const location = useLocation();

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [meetings, setMeetings] = useState<Meeting[]>([]);

	const [isActiveBoardExist, setIsActiveBoardExist] = useState<boolean>(false);
	const [serviceCostLoading, setServiceCostLoading] = useState<boolean>(true);
	const [serviceCost, setServiceCost] = useState<ServiceCost>();

	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [selectedRequestId, setSelectedRequestId] = useState<number>();

	const [cancelModalOpen, setCancelModalOpen] = useState<boolean>(false);
	const [votingModalOpen, setVotingModalOpen] = useState<boolean>(false);
	const [attendanceModalOpen, setAttendanceModalOpen] = useState<boolean>(false);
	const [closeVoteModalOpen, setCloseVoteModalOpen] = useState(false);
	const [endMeetingModalOpen, setEndMeetingModalOpen] = useState(false);

	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords, setTotalRecords] = useState<number>(0);

	const columns: TableProps<Meeting>['columns'] = [
		{
			title: t('ACTIONS.CODE'),
			dataIndex: 'meeting_number',
			key: 'meeting_number',
		},
		{
			title: t('ACTIONS.CHAIRMAN'),
			dataIndex: 'chairman',
			key: 'chairman',
		},
		{
			title: t('COMMON.STATUS'),
			dataIndex: 'status',
			key: 'status',
			render: (status) => <StatusComponent text={t(`COMMON.${status.toUpperCase()}`)} color={StatusColors[status]} />,
		},
		{
			title: t('ACTIONS.MEETING_DATE'),
			dataIndex: 'date',
			key: 'date',
			render: (date) => <span>{dateFormat(date)}</span>,
		},
		{
			title: t('ACTIONS.TYPE'),
			dataIndex: 'translated_type',
			key: 'translated_type',
			render: (translated_type) => <span>{isRTL() ? translated_type.ar : translated_type.en}</span>,
		},
		{
			title: t('COMMON.ACTIONS'),
			dataIndex: '_',
			key: '_',
			render: (_, row) => (
				<Flex gap={10} justify="center">
					<ListButton type="view" text={t('COMMON.VIEW')} onClick={() => navigate(`view/${row.id}`)} />

					{row.can_edit_or_cancel && (
						<>
							<ListButton type="edit" {...suspendedProps} onClick={() => navigate(`${row.id}`)} text={t('COMMON.EDIT')} />
							<ListButton
								type="reject"
								{...suspendedProps}
								onClick={() => {
									setSelectedRequestId(row.id);
									setCancelModalOpen(true);
								}}
								text={t('COMMON.CANCEL')}
							/>
						</>
					)}

					{row.can_open_meeting_attendance && (
						<ListButton
							type="openAttendance"
							{...suspendedProps}
							onClick={() => {
								setSelectedRequestId(row.id);
								setAttendanceModalOpen(true);
							}}
							text={t('ACTIONS.OPEN_ATTENDANCE')}
						/>
					)}

					{row.can_open_voting && (
						<ListButton
							type="vote"
							{...suspendedProps}
							onClick={() => {
								setSelectedRequestId(row.id);
								setVotingModalOpen(true);
							}}
							text={t('ACTIONS.OPEN_VOTING')}
						/>
					)}

					{row.can_close_meeting && (
						<ListButton
							type="endMeeting"
							{...suspendedProps}
							onClick={() => {
								setSelectedRequestId(row.id);
								setEndMeetingModalOpen(true);
							}}
							text={t('ACTIONS.END_MEETING')}
						/>
					)}

					{row.can_close_voting && (
						<ListButton
							type="endVote"
							{...suspendedProps}
							onClick={() => {
								setSelectedRequestId(row.id);
								setCloseVoteModalOpen(true);
							}}
							text={t('ACTIONS.CLOSE_VOTING')}
						/>
					)}
				</Flex>
			),
		},
	];

	useEffect(() => {
		getServiceCost();
		getMeetings();
	}, []);

	const getCurrentPage = () => {
		const params = new URLSearchParams(location.search);
		const page = params.get('page');
		return page ? parseInt(page) : 1;
	};

	const getServiceCost = async () => {
		try {
			setServiceCostLoading(true);

			const response = await onGetServiceCost('board_meetings');
			setServiceCost(response?.success);

			setServiceCostLoading(false);
		} catch (error) {
			setServiceCostLoading(false);
		}
	};

	const getMeetings = async () => {
		try {
			setIsLoading(true);

			const response = await onGetMeetingsForActiveBoard(getCurrentPage());
			setMeetings(response.meetings);
			setTotalRecords(response.pagination.total);

			setIsLoading(false);
			setIsActiveBoardExist(true);
		} catch (error: any) {
			console.log(error);
			if (error?.response?.data?.message === 'No active board found for this company') {
				setIsLoading(false);
				setIsActiveBoardExist(false);
				return;
			}

			setMeetings([]);
			setTotalRecords(0);
			setIsLoading(false);
		}
	};

	const fetchMoreData = async (page: number) => {
		try {
			const params = new URLSearchParams(location.search);
			params.set('page', page.toString());
			navigate(`${location.pathname}?${params.toString()}`, { replace: true });

			setIsLoading(true);
			window.scrollTo({ behavior: 'smooth', top: 0 });

			const response = await onGetMeetingsForActiveBoard(getCurrentPage());
			setMeetings(response.meetings);

			setCurrentPage(page);
			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			setIsLoading(false);
		}
	};

	const cancelHandler = async () => {
		try {
			setIsSubmitting(true);
			await onCancelMeeting(selectedRequestId as number);
			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.MEETING_CANCELLED'));
			setCancelModalOpen(false);
			setIsSubmitting(false);
			getMeetings();
			setSelectedRequestId(undefined);
		} catch (error) {
			setIsSubmitting(false);
			NotificationService.error(t('COMMON.ERROR'), t('ACTIONS.MEETING_NOT_CANCELLED'));
		}
	};

	const openVoting = async () => {
		try {
			setIsSubmitting(true);
			await onStartMeetingVoting(selectedRequestId as number);
			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.VOTING_OPENED_SUCCESS'));
			setVotingModalOpen(false);
			setIsSubmitting(false);
			getMeetings();
			setSelectedRequestId(undefined);
		} catch (error) {
			setIsSubmitting(false);
			NotificationService.error(t('COMMON.ERROR'), t('ACTIONS.VOTING_OPENED_FAILED'));
		}
	};

	const openAttendance = async () => {
		try {
			setIsSubmitting(true);
			await onStartMeetingAttendance(selectedRequestId as number);
			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.ATTENDANCE_OPENED_SUCCESS'));
			setAttendanceModalOpen(false);
			setIsSubmitting(false);
			getMeetings();
			setSelectedRequestId(undefined);
		} catch (error) {
			setIsSubmitting(false);
			NotificationService.error(t('COMMON.ERROR'), t('ACTIONS.ATTENDANCE_OPENED_FAILED'));
		}
	};

	const closeVoteHandler = async () => {
		try {
			setIsSubmitting(true);
			await onEndMeetingVoting(selectedRequestId as number);
			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.VOTING_CLOSING_SUCCESS'));
			setCloseVoteModalOpen(false);
			setIsSubmitting(false);
			setSelectedRequestId(undefined);
			getMeetings();
		} catch (error) {
			setIsSubmitting(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.SUBMIT_ERROR'));
		}
	};

	const endMeetingHandler = async () => {
		try {
			setIsSubmitting(true);
			await onCloseMeeting(selectedRequestId as number);
			NotificationService.success(t('ACTIONS.MEETING_ENDED_SUCCESSFULLY'));
			setEndMeetingModalOpen(false);
			setIsSubmitting(false);
			setSelectedRequestId(undefined);
			getMeetings();
		} catch (error) {
			setIsSubmitting(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.SUBMIT_ERROR'));
		}
	};

	return (
		<>
			<ActionListHeaderComponent
				title={t('ACTIONS.BOARDS_MEETINGS')}
				subTitle={t('ACTIONS.BOARDS_DESC')}
				costLoading={serviceCostLoading || isLoading}
				costs={[serviceCost!]}
				newRequestText={t('ACTIONS.NEW_MEETING')}
				addAvailable={isActiveBoardExist}
				addAvailableText={t('ACTIONS.ADD_ACTIVE_BOARD_TO_ADD_MEETING')}
			/>

			<Table
				columns={columns}
				dataSource={meetings}
				className="bordered"
				scroll={{ x: 600 }}
				rowKey="id"
				loading={isLoading}
				locale={{ emptyText: <NoData /> }}
				pagination={{
					pageSize: 15,
					total: totalRecords,
					showSizeChanger: false,
					current: currentPage,
					onChange: fetchMoreData,
					showTotal: (total) => <TableTotal total={total} />,
				}}
			/>

			{/* CANCEL MODAL */}
			<ConfirmModalComponent
				open={cancelModalOpen}
				confirmText={t('COMMON.CONFIRM')}
				onConfirm={cancelHandler}
				isLoading={isSubmitting}
				onDiscard={() => setCancelModalOpen(false)}
				title={t('ACTIONS.CANCEL_MEETING')}
				description={t('ACTIONS.CONFIRM_CANCEL_MEETING')}
				descriptionClasses="text-center"
			/>

			{/* VOTING MODAL */}
			<ConfirmModalComponent
				open={votingModalOpen}
				confirmText={t('COMMON.CONFIRM')}
				onConfirm={openVoting}
				isLoading={isSubmitting}
				onDiscard={() => setVotingModalOpen(false)}
				title={t('ACTIONS.OPEN_VOTING')}
				description={t('ACTIONS.CONFIRM_OPEN_VOTING')}
				descriptionClasses="text-center"
			/>

			{/* ATTENDANCE MODAL */}
			<ConfirmModalComponent
				open={attendanceModalOpen}
				confirmText={t('COMMON.CONFIRM')}
				onConfirm={openAttendance}
				isLoading={isSubmitting}
				onDiscard={() => setAttendanceModalOpen(false)}
				title={t('ACTIONS.OPEN_ATTENDANCE')}
				description={t('ACTIONS.CONFIRM_OPEN_ATTENDANCE')}
				descriptionClasses="text-center"
			/>

			{/* END VOTING MODAL */}
			<ConfirmModalComponent
				open={closeVoteModalOpen}
				confirmText={t('COMMON.CONFIRM')}
				onConfirm={closeVoteHandler}
				isLoading={isSubmitting}
				onDiscard={() => setCloseVoteModalOpen(false)}
				title={t('ACTIONS.CLOSE_VOTING')}
				description={t('ACTIONS.CONFIRM_CLOSE_VOTING')}
				descriptionClasses="text-center"
			/>

			{/* END MEETING MODAL */}
			<ConfirmModalComponent
				open={endMeetingModalOpen}
				confirmText={t('COMMON.CONFIRM')}
				onConfirm={endMeetingHandler}
				isLoading={isSubmitting}
				onDiscard={() => setEndMeetingModalOpen(false)}
				title={t('ACTIONS.END_MEETING')}
				description={t('ACTIONS.CONFIRM_END_MEETING')}
				descriptionClasses="text-center"
			/>
		</>
	);
};

export default BoardMeetingsListPage;
